import React, { useEffect } from "react";
import { Plus } from "lucide-react";
import NumberInput from "@/components/NumberInput";
import HotelCategorySelect from "@/components/HotelCategorySelect";
import HotelForm from "@/components/HotelForm";
import { F } from "../_shared";

export default function StepHotels({ q, setQ, cityMap }) {
  const add = () => setQ((p) => {
    const idx = (p.hotels || []).length;
    const dest = (p.destinations && p.destinations.length ? p.destinations[0] : p.destination) || {};
    const cities = p.cities || [];
    const stop = cities[idx] || cities[0] || {};
    const prefCity = stop.city || stop.name || "";
    return { ...p, hotels: [...p.hotels, {
      country_code: dest.country_code || "", country: dest.country || "",
      city: prefCity,
      name: "", image_url: "", rating: 0, address: "", hotel_place_id: "",
      hotel_category: "", room_category: "", meal_plan: "",
      check_in: "", check_out: "", rooms: 1, nights: 2,
    }] };
  });
  const upd = (i, patch) => setQ((p) => ({ ...p, hotels: p.hotels.map((h, idx) => idx === i ? patch : h) }));
  const updField = (i, k, v) => setQ((p) => ({ ...p, hotels: p.hotels.map((h, idx) => idx === i ? { ...h, [k]: v } : h) }));
  const rem = (i) => setQ((p) => ({ ...p, hotels: p.hotels.filter((_, idx) => idx !== i) }));

  // Back-fill blank country/city on existing hotels from the destination + cities list
  useEffect(() => {
    const dest = (q.destinations && q.destinations.length ? q.destinations[0] : q.destination) || {};
    if (!dest.country_code && !dest.country && !(q.cities || []).length) return;
    const cities = q.cities || [];
    let changed = false;
    const next = (q.hotels || []).map((h, i) => {
      const patch = {};
      if (!h.country && dest.country) patch.country = dest.country;
      if (!h.country_code && dest.country_code) patch.country_code = dest.country_code;
      if (!h.city) {
        const stop = cities[i] || cities[0];
        const c = stop && (stop.city || stop.name);
        if (c) patch.city = c;
      }
      if (Object.keys(patch).length) { changed = true; return { ...h, ...patch }; }
      return h;
    });
    if (changed) setQ((p) => ({ ...p, hotels: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    (q.destination || {}).country_code, (q.destination || {}).country,
    JSON.stringify((q.cities || []).map((c) => c.city || c.name || "")),
    JSON.stringify((q.hotels || []).map((h) => `${h.country}|${h.city}`)),
  ]);

  return (
    <>
      {/* Auto-calculate toggle + city timeline */}
      <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-emerald-900 flex items-center gap-2">
              Auto-calculate hotel dates
            </div>
            <div className="text-xs text-emerald-800/80 mt-1">
              When ON, each hotel's check-in / check-out is derived from the Package Start Date and the city's nights.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              data-testid="auto-hotel-dates-toggle"
              type="checkbox"
              checked={q.auto_hotel_dates !== false}
              onChange={(e) => setQ((p) => ({ ...p, auto_hotel_dates: e.target.checked }))}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-slate-300 peer-checked:bg-[#16A34A] rounded-full transition relative">
              <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5" style={{ transform: q.auto_hotel_dates !== false ? "translateX(20px)" : "translateX(0)" }} />
            </div>
            <span className="text-xs font-semibold text-emerald-900">{q.auto_hotel_dates !== false ? "ON" : "OFF"}</span>
          </label>
        </div>
        {q.auto_hotel_dates !== false && q.start_date && Object.keys(cityMap || {}).length > 0 && (
          <div data-testid="city-timeline" className="mt-4 flex flex-wrap gap-2">
            {Object.entries(cityMap).map(([city, info]) => (
              <div key={city} className="bg-white border border-emerald-200 rounded px-3 py-2 text-[11px]">
                <div className="font-semibold text-slate-900">{city}</div>
                <div className="text-slate-500 mt-0.5">
                  {new Date(info.check_in + "T00:00:00Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" })}
                  <span className="mx-1 text-emerald-600">→</span>
                  {new Date(info.check_out + "T00:00:00Z").toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" })}
                  <span className="ml-2 text-emerald-700 font-semibold">{info.nights}N</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {q.auto_hotel_dates !== false && !q.start_date && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Set the Package Start Date in <b>Tour Dates</b> to enable auto-calculated hotel dates.
          </div>
        )}
      </div>

      <div className="space-y-4">
        {q.hotels.map((h, i) => (
          <HotelForm
            key={h.hotel_place_id || `h-${i}`}
            hotel={h}
            index={i}
            onChange={(next) => upd(i, next)}
            onRemove={() => rem(i)}
            CategorySlot={
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Hotel Category (Star rating)</label>
                  <HotelCategorySelect value={h.hotel_category} onChange={(v) => updField(i, "hotel_category", v)} testid={`hotel-cat-${i}`} />
                </div>
                <div />
              </div>
            }
            DateFieldsSlot={
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Check-in" type="date" value={h.check_in}
                   onChange={(v) => { setQ((p) => ({ ...p, auto_hotel_dates: false })); updField(i, "check_in", v); }}
                   testid={`hotel-in-${i}`} />
                <F label="Check-out" type="date" value={h.check_out}
                   onChange={(v) => { setQ((p) => ({ ...p, auto_hotel_dates: false })); updField(i, "check_out", v); }}
                   testid={`hotel-out-${i}`} />
              </div>
            }
            RoomsNightsSlot={
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Rooms</div>
                  <NumberInput testid={`hotel-rooms-${i}`} value={h.rooms} onChange={(v) => updField(i, "rooms", v)} min={1} max={50} placeholder="1" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 mb-1.5">Nights</div>
                  <NumberInput testid={`hotel-nights-${i}`} value={h.nights} onChange={(v) => updField(i, "nights", v)} min={1} max={365} placeholder="1" />
                </div>
              </div>
            }
          />
        ))}
      </div>
      <button data-testid="hotel-add-btn" onClick={add} className="mt-4 inline-flex items-center gap-2 text-sm px-3 py-2 border border-dashed border-slate-300 rounded hover:border-[#16A34A] hover:text-[#16A34A]"><Plus size={14} /> Add hotel</button>
    </>
  );
}
